import { z } from 'zod';

/*
 * Buying credit packs in the Mini App (spec 2026-10-12 «Оплата»): Kaspi through Aitole,
 * a Russian card, SBP or a foreign card through lava.top, Telegram Stars.
 */

export const payMethodSchema = z.enum(['kaspi', 'lava_card', 'lava_sbp', 'lava_usd', 'stars']);
export type PayMethod = z.infer<typeof payMethodSchema>;

export const payCurrencySchema = z.enum(['KZT', 'RUB', 'USD', 'XTR']);
export type PayCurrency = z.infer<typeof payCurrencySchema>;

export const orderStatusSchema = z.enum(['pending', 'paid', 'failed', 'expired', 'refunded', 'chargeback']);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

const orderCode = z.string().regex(/^[A-Z2-9]{6}$/);
const money = z.number().int().nonnegative();

/** A pack as the person sees it: only the methods offered right now. */
export const packDtoSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  credits: z.number().int().positive(),
  /** Whole tenge, roubles, dollars, stars; 0 = not sold for that currency. */
  prices: z.object({ kzt: money, rub: money, usd: money, stars: money }),
  methods: z.array(payMethodSchema),
});
export type PackDto = z.infer<typeof packDtoSchema>;

/** GET /payments/options */
export const paymentOptionsResponseSchema = z.object({
  packs: z.array(packDtoSchema),
  /** The e-mail typed for lava.top last time. */
  email: z.string().nullable(),
  offerUrl: z.string(),
});
export type PaymentOptionsResponse = z.infer<typeof paymentOptionsResponseSchema>;

/** POST /payments/orders */
export const startPurchaseRequestSchema = z.object({
  packId: z.number().int().positive(),
  method: payMethodSchema,
  /** Kaspi: the number the invoice goes to. */
  phone: z.string().max(32).optional(),
  /** lava.top: where the receipt goes. */
  email: z.string().max(254).optional(),
});
export type StartPurchaseRequest = z.infer<typeof startPurchaseRequestSchema>;

export const startPurchaseResponseSchema = z.discriminatedUnion('kind', [
  /** The invoice is in the person's Kaspi app; `amount` in whole tenge. */
  z.object({ kind: z.literal('kaspi_sent'), code: orderCode, mask: z.string(), amount: z.number().int().positive() }),
  /** The lava.top payment page, opened in the outer browser. */
  z.object({ kind: z.literal('lava_link'), code: orderCode, url: z.string(), expiresAt: z.string() }),
  /** The Telegram invoice link for WebApp.openInvoice. */
  z.object({ kind: z.literal('stars_link'), code: orderCode, url: z.string() }),
  /** The Kaspi invoice call failed twice: if the invoice came anyway, paying it still counts. */
  z.object({ kind: z.literal('unsure'), code: orderCode }),
  /** Kaspi refused the invoice with its own words. */
  z.object({ kind: z.literal('refused'), message: z.string() }),
  z.object({ kind: z.literal('not_kaspi_client') }),
  z.object({ kind: z.literal('kaspi_down') }),
  z.object({ kind: z.literal('too_many') }),
  z.object({ kind: z.literal('unavailable') }),
  z.object({ kind: z.literal('bad_phone') }),
  z.object({ kind: z.literal('bad_email') }),
  z.object({ kind: z.literal('error') }),
]);
export type StartPurchaseResponse = z.infer<typeof startPurchaseResponseSchema>;

/** GET /payments/orders/:code */
export const orderStatusResponseSchema = z.object({
  code: orderCode,
  status: orderStatusSchema,
  credits: z.number().int(),
  /** The balance once the order is paid. */
  balance: z.number().int().nullable(),
});
export type OrderStatusResponse = z.infer<typeof orderStatusResponseSchema>;

// ---- admin ----

export const packVisibilitySchema = z.enum(['all', 'admins']);
const price = z.number().int().min(0).max(10_000_000);

/** POST /admin/packs; PATCH takes any part of it. */
export const adminPackInputSchema = z.object({
  title: z.string().trim().min(1).max(60),
  credits: z.number().int().min(1).max(100_000),
  priceKzt: price,
  priceRub: price,
  priceUsd: price,
  priceStars: price,
  visibility: packVisibilitySchema,
  sort: z.number().int().min(-1_000_000).max(1_000_000),
  active: z.boolean(),
});
export type AdminPackInput = z.infer<typeof adminPackInputSchema>;

export const adminPackPatchSchema = adminPackInputSchema.partial();
export type AdminPackPatch = z.infer<typeof adminPackPatchSchema>;

export const adminPackSchema = adminPackInputSchema.extend({
  id: z.number().int(),
  updatedAt: z.string(),
});
export type AdminPack = z.infer<typeof adminPackSchema>;

/** GET /admin/packs */
export const adminPacksResponseSchema = z.object({ packs: z.array(adminPackSchema) });
export type AdminPacksResponse = z.infer<typeof adminPacksResponseSchema>;

export const paymentTotalsRowSchema = z.object({
  method: payMethodSchema,
  currency: payCurrencySchema,
  period: z.enum(['today', 'month']),
  paidCount: z.number().int(),
  paidSum: z.number().int(),
  refundCount: z.number().int(),
  refundSum: z.number().int(),
});
export type PaymentTotalsRowDto = z.infer<typeof paymentTotalsRowSchema>;

export const adminOrderDtoSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  telegramId: z.number().int(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
  packTitle: z.string(),
  credits: z.number().int(),
  method: payMethodSchema,
  amount: z.number().int(),
  currency: payCurrencySchema,
  status: orderStatusSchema,
  phoneMask: z.string().nullable(),
  email: z.string().nullable(),
  /** What a full refund or a chargeback took back. */
  revokedCredits: z.number().int().nullable(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  closedAt: z.string().nullable(),
});
export type AdminOrderDto = z.infer<typeof adminOrderDtoSchema>;

/** GET /admin/payments */
export const adminPaymentsResponseSchema = z.object({
  totals: z.array(paymentTotalsRowSchema),
  orders: z.array(adminOrderDtoSchema),
});
export type AdminPaymentsResponse = z.infer<typeof adminPaymentsResponseSchema>;

/** POST /admin/payments/:code/refund-stars */
export const adminRefundStarsResponseSchema = z.object({
  credits: z.number().int(),
  taken: z.number().int(),
  spent: z.number().int(),
});
export type AdminRefundStarsResponse = z.infer<typeof adminRefundStarsResponseSchema>;
